import { ReactNode } from 'react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route, BrowserRouter as Router, RouterProvider, Routes } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license';

import { AuthProvider, DefaultTheme, ErrorHandling, OverlayProvider, SnackbarProvider } from '@sagecentral/core-ui';
import { PreferenceType } from 'components/preferences';
import { NavigateDefaultTab } from 'components/shared';
import WorkItemBulkUpdate, { BulkUpdateModes } from 'components/bulk-update/WorkItemBulkUpdate';

import { CustomCircularProgress, LayoutWithOutlet } from './components/shared/layout';
import { TeamGrid } from 'components/teams/grid';
import AuthRoutes from 'pages/auth';
import WorkItemRoutes from 'pages/work-items';

import '@sagecentral/ckeditor/dist/style.css';
import { RequireTeamPermission, TeamPermissionsProvider } from 'components/teams';
import TaskRoutes from 'pages/tasks';
import { BulkUpdatePermissionsProvider, WorkItemBulkUpdateResources } from 'components/bulk-update';
import { useBulkUpdateWorkItemPermissions } from 'hooks/work-items';
import { CreateDashboard } from 'components/create';
import TrackPreviousUrl from 'TrackLocation';
import AccountPermissionsProvider from 'components/auth/AccountPermissionsProvider';
import FeatureGate from 'components/shared/FeatureGate';
import ProjectRoutes from 'pages/projects';
import ExternalPageOpener from 'components/shared/layout/ExtrenalLinkOpener';

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY as string);

type RequireBulkUpdatePermissionProps = {
  permission: string[],
  children: ReactNode
};

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY as string);

const checkBulkUpdatedFeatureEnabled = () => {
  const disabledFeatures = process.env.REACT_APP_DISABLED_FEATURES;
  console.log(disabledFeatures);
  if (disabledFeatures && disabledFeatures.includes("BulkUpdateWorkItemStatus")) {
    return false;
  }
  return true;
};

const RequireBulkUpdatePermission = (props: RequireBulkUpdatePermissionProps) => {
  const permissions = useBulkUpdateWorkItemPermissions() as any;
  if (permissions == null) return <CustomCircularProgress />;
  const authorized = (permissions[props.permission[0]] && permissions[props.permission[1]]) || false;
  return authorized ? <Outlet /> : <ErrorHandling error={{ message: "Unauthorized ", name: "Error", cause: 403 }} />;
};

const LocationLayout = () => {
  return (
    <>
      <TrackPreviousUrl />
      <Outlet />
    </>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<LocationLayout />} >
      <Route path="/*" element={<AuthRoutes />} />
      <Route path="/maintenance" Component={() => {
        window.location.href = process.env.REACT_APP_OFFLINE_PAGE_URL ?? "";
        return <></>;
      }} />
      <Route path="/external" element={<ExternalPageOpener />} />

      <Route element={
        <FeatureGate flag="maintenance-mode">
          <FeatureGate.Content>
            <Navigate to="/maintenance" relative='path' />
          </FeatureGate.Content>
          <FeatureGate.AltContent>
            <LayoutWithOutlet />
          </FeatureGate.AltContent>
        </FeatureGate>} >
        <Route index element={<Navigate to="/tasks/my" replace={true} />} />
        <Route path="tasks/*" element={<TaskRoutes />} />
        <Route path="projects/*" element={<ProjectRoutes />}>
        </Route>
        <Route path="team/*" element={<TeamPermissionsProvider><Outlet /></TeamPermissionsProvider>}>
          <Route path='my/*' element={<RequireTeamPermission permission="isTeamOwner"><Outlet /></RequireTeamPermission>}>
            <Route index element={<NavigateDefaultTab type={PreferenceType.MyTeamTaskDashboard} fallback={<Navigate to="task" />} />} />
            <Route path="task" element={<TeamGrid tab="tasks" />} />
            <Route path="work-item" element={<TeamGrid tab="work-items" />} />
          </Route>
        </Route>
        <Route path="create/*" element={
          <AccountPermissionsProvider>
            <CreateDashboard />
          </AccountPermissionsProvider>
        } />
        <Route path="bulk-update/*" element={
          <BulkUpdatePermissionsProvider>
            <Outlet />
          </BulkUpdatePermissionsProvider>}>
          <Route index element={<Navigate to="work-item/status" replace={true} />} />
          <Route path="work-item/*">
            <Route index element={<Navigate to="status" replace={true} />} />
            <Route path="status" element=
              {
                <RequireBulkUpdatePermission permission={["canAccessWorkItemStatus", "isWorkItemStatusFeatureEnabled"]}>
                  <Outlet />
                </RequireBulkUpdatePermission>
              }>
              <Route index element={<WorkItemBulkUpdate mode={BulkUpdateModes.Status} />} />
            </Route>
            <Route path="resources" element=
              {
                <RequireBulkUpdatePermission permission={["canAccessWorkItemResource", "isWorkItemResourceFeatureEnabled"]}>
                  <Outlet />
                </RequireBulkUpdatePermission>
              }>
              <Route index element={<WorkItemBulkUpdateResources />} />
            </Route>
          </Route>
        </Route>
        <Route path="/error" element={<ErrorHandling error={{ message: "Base error", name: "Error", cause: 404 }} />} />
        <Route path="*" element={<ErrorHandling error={{ message: "Base error", name: "Error", cause: 404 }} />} />
        <Route path="*" element={<ErrorHandling error={{ message: "Base error", name: "Error", cause: 404 }} />} />
        <Route path="/error" element={<ErrorHandling error={{ message: "Base error", name: "Error", cause: 404 }} />} />
        <Route path="*" element={<ErrorHandling error={{ message: "Base error", name: "Error", cause: 404 }} />} />
        <Route path="workitems/*" element={<WorkItemRoutes />} />
      </Route>
    </Route>
  )
);
const App =
  () => {
    return (
      <ThemeProvider theme={DefaultTheme}>
        <CssBaseline />
        <AuthProvider>
          <OverlayProvider>
            <SnackbarProvider>
              <RouterProvider router={router} />
            </SnackbarProvider>
          </OverlayProvider>
        </AuthProvider>
      </ThemeProvider>
    );
  }
export default App;